import { useContext, useMemo } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../../General/AppPreferencesProvider";
import { AI_INSIGHTS_FEED_PATH } from "../../CustomizedDashboard/dashboardUrl";
import { ANALYST_RUNS_PATH } from "../../AnalystRuns/analystRunsRoutes";
import {
  buildSeemplicityFindingHref,
  useFindingEntity,
} from "../../Findings/findingEntity";
import type { NavIcon } from "./navIcon";
import {
  matchesPathPrefix,
  type AppsMenuEntry,
} from "./AppsMenu/appsMenuConfig";

export interface NavLinkItem {
  id: string;
  label: string;
  to: string;
  icon: NavIcon;
  matchSegments: string[];
  excludePaths?: string[];
  includePaths?: string[];
}

export type NavGroupId = "overview" | "exposure" | "response";

export interface NavGroup {
  id: NavGroupId;
  caption: string;
  items: NavLinkItem[];
}

export interface GroupedNavConfig {
  groups: NavGroup[];
  appsMenu: AppsMenuEntry[];
  seema: { to: string } | null;
}

export interface GroupedNavConfigOptions {
  useAI: boolean;
  isAllowed: (action: string) => boolean;
  hideAggregationRulesPage: boolean;
  remediationItemsHref: string;
}

export const SEEMA_PATH = "/seema/";

const AI_ICON: NavIcon = {
  kind: "font",
  className: "seem-icon-new-actions",
};

export const isNavItemActive = (
  pathname: string,
  item: NavLinkItem
): boolean => {
  if (item.includePaths?.some((path) => matchesPathPrefix(pathname, path))) {
    return true;
  }

  if (item.excludePaths?.some((path) => matchesPathPrefix(pathname, path))) {
    return false;
  }

  const [firstSegment] = pathname.split("/").filter(Boolean);

  return !!firstSegment && item.matchSegments.includes(firstSegment);
};

export const buildGroupedNavConfig = ({
  useAI,
  isAllowed,
  hideAggregationRulesPage,
  remediationItemsHref,
}: GroupedNavConfigOptions): GroupedNavConfig => {
  const groups: NavGroup[] = [
    {
      id: "overview",
      caption: "Overview",
      items: [
        {
          id: "dashboard",
          label: "Dashboard",
          to: "/dashboard/",
          icon: { kind: "font", className: "seem-icon-widgets" },
          matchSegments: ["dashboard"],
          excludePaths: [AI_INSIGHTS_FEED_PATH],
        },
        ...(useAI
          ? [
              {
                id: "ai-insights",
                label: "AI Insights",
                to: AI_INSIGHTS_FEED_PATH,
                icon: AI_ICON,
                matchSegments: [],
                includePaths: [AI_INSIGHTS_FEED_PATH],
              },
            ]
          : []),
      ],
    },
    {
      id: "exposure",
      caption: "Exposure",
      items: [
        {
          id: "resources",
          label: "Resources",
          to: "/resources/list",
          icon: { kind: "font", className: "seem-icon-raw-resource" },
          matchSegments: ["resources"],
        },
        {
          id: "findings",
          label: "Findings",
          to: buildSeemplicityFindingHref(),
          icon: { kind: "font", className: "seem-icon-manualfinding" },
          matchSegments: ["seemplicity-findings"],
        },
      ],
    },
    {
      id: "response",
      caption: "Response",
      items: [
        {
          id: "remediation-items",
          label: "Remediation Items",
          to: remediationItemsHref,
          icon: { kind: "font", className: "seem-icon-remediation" },
          matchSegments: ["remediation-items", "findings"],
        },
        {
          id: "automations",
          label: "Automations",
          to: "/remediation",
          icon: { kind: "font", className: "seem-icon-automations" },
          matchSegments: ["remediation", "remediationQueue"],
        },
      ],
    },
  ];

  const appsMenu: AppsMenuEntry[] = [
    {
      id: "findings-validation",
      label: "Findings Validation",
      to: "/discovery/findings-validation",
      icon: { kind: "font", className: "seem-icon-manualfinding" },
      matchPrefixes: ["/discovery"],
    },
  ];

  if (isAllowed(userActionsEnum.VIEW_RULES_PAGE)) {
    appsMenu.push({
      id: "rules",
      label: "Rules",
      icon: { kind: "font", className: "seem-icon-rules" },
      children: [
        {
          id: "findings-rules",
          label: "Findings Rules",
          to: "/policies/findings-rules/",
          matchPrefixes: ["/policies/findings-rules"],
        },
        ...(!hideAggregationRulesPage
          ? [
              {
                id: "aggregation-rules",
                label: "Aggregation Rules",
                to: "/policies/aggregation-rules",
                matchPrefixes: ["/policies/aggregation-rules"],
              },
            ]
          : []),
        {
          id: "ticket-sla-rules",
          label: "Ticket SLA Rules",
          to: "/policies/ticket-sla-rules",
          matchPrefixes: ["/policies/ticket-sla-rules"],
        },
      ],
    });
  }

  if (useAI && isAllowed(userActionsEnum.VIEW_SEEM_CHAT)) {
    appsMenu.push({
      id: "ai-analysts",
      label: "AI Analysts",
      to: ANALYST_RUNS_PATH,
      icon: AI_ICON,
      matchPrefixes: [ANALYST_RUNS_PATH],
    });
  }

  return {
    groups: groups.filter((group) => group.items.length > 0),
    appsMenu,
    seema: useAI ? { to: SEEMA_PATH } : null,
  };
};

export const useGroupedNavConfig = (): GroupedNavConfig => {
  const { hideAggregationRulesPage } = useFlags();
  const {
    appPreferences: { useAI },
    isAllowed,
  } = useContext(AppPreferencesContext);
  const { buildRemediationItemHref } = useFindingEntity();

  return useMemo(
    () =>
      buildGroupedNavConfig({
        useAI: !!useAI,
        isAllowed,
        hideAggregationRulesPage: !!hideAggregationRulesPage,
        remediationItemsHref: buildRemediationItemHref(),
      }),
    [useAI, isAllowed, hideAggregationRulesPage, buildRemediationItemHref]
  );
};
